import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
} from "@mui/material";
import { useRebuildIndex, useRebuildStatus } from "../../api/queries";
import { IndexErrorRows } from "./IndexErrorRows";

export const AdminDashboard = () => {
  const { data: status, isLoading: isStatusLoading } = useRebuildStatus();
  const { mutate: rebuild, isPending: isRebuildingTrigger } = useRebuildIndex();
  const [error, setError] = useState<string | null>(null);

  const handleRebuild = () => {
    if (
      confirm(
        "Are you sure you want to rebuild the index? This may take some time.",
      )
    ) {
      setError(null);
      rebuild(undefined, {
        onError: (e) => setError(e.message),
      });
    }
  };

  if (isStatusLoading) return <LinearProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Maintenance
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transaction Index
        </Typography>
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            color: "text.secondary",
          }}
        >
          The transaction index is a projection of the stored transaction
          events. If you encounter inconsistencies or have updated the
          projection logic, you can rebuild it here.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRebuild}
            disabled={status?.isRebuilding || isRebuildingTrigger}
          >
            {status?.isRebuilding ? "Rebuilding..." : "Rebuild Index"}
          </Button>
          {status?.isRebuilding && (
            <Typography variant="caption">In Progress</Typography>
          )}
        </Box>

        {status?.isRebuilding && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={
                status.total > 0 ? (status.progress / status.total) * 100 : 0
              }
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
              {status.progress} / {status.total} transactions processed
            </Typography>
          </Box>
        )}
        {!status?.isRebuilding && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            Logic Version: {status?.logicVersion} | Total Transactions:{" "}
            {status?.total || status?.progress}
          </Typography>
        )}
      </Paper>
      {status && status.errorRowCount > 0 && (
        <IndexErrorRows rows={status.errorRows} total={status.errorRowCount} />
      )}
    </Box>
  );
};
